<template>
  <transition name="wt2-slide-right">
    <div v-if="panelOpen && group" class="wt-version-history-panel wt2-sg">
      <header class="-header">
        <div class="wt2-text-xl">
          {{ 'storyVersions.versionHistory' | culture }}
        </div>
        <wt-close @click.native="close" />
      </header>

      <div v-if="isViewingOlder" class="-newer-banner">
        <i class="wticon wticon-warning2" />
        <div>
          <strong>{{ 'storyVersions.newerAvailableTitle' | culture }}</strong>
          {{ newerBannerText }}
        </div>
      </div>

      <div class="-list">
        <div
          v-for="row in sortedVersions"
          :key="row.storyId"
          class="-version-row"
          :class="{
            '-active': row.storyId === openStoryId,
            '-locked': isLocked(row),
          }"
          @click="loadVersion(row)"
        >
          <div class="-thumb">
            <img v-if="posterFor(row)" :src="posterFor(row)" />
            <div v-else class="-no-thumb" />
          </div>

          <div class="-body">
            <div class="-title" :title="titleFor(row)">
              <i v-if="row.type === 'VO'" class="wticon wticon-mic -vo" />
              {{ titleFor(row) }}
            </div>

            <div
              v-if="chipFor(row)"
              class="-chip"
              :class="chipClass(row)"
              v-text="chipLabel(row)"
            />

            <div v-if="row.latestAiPrompt" class="-ai-prompt-wrap">
              <div
                ref="promptEl"
                class="-ai-prompt"
                :class="{ '-expanded': isPromptExpanded(row) }"
                :data-story-id="row.storyId"
                v-text="row.latestAiPrompt"
              />
              <a
                v-if="isPromptClamped(row) || isPromptExpanded(row)"
                class="-ai-prompt-toggle"
                @click.stop="togglePrompt(row)"
                >{{
                  (isPromptExpanded(row)
                    ? 'storyVersions.showLess'
                    : 'storyVersions.showMore') | culture
                }}</a
              >
            </div>

            <div class="-meta" v-text="metaFor(row)" />

            <div v-if="isLocked(row)" class="-load-hint -locked-hint">
              <i class="wticon wticon-lock2" />
              {{ 'storyPreview.inUse' | culture }} {{ lockerNameFor(row) }}
            </div>
            <div v-else-if="row.storyId !== openStoryId" class="-load-hint">
              {{ 'storyVersions.clickToLoad' | culture }}
            </div>

            <a
              v-if="!row.root"
              class="-branch"
              :class="{ '-disabled': isBranchDisabled(row) }"
              @click.stop="branch(row)"
              >{{ 'storyVersions.branch' | culture }}</a
            >
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import app from 'app';
import Analytics from 'c/WtAnalytics';
import { mapGetters } from 'vuex';

export default {
  name: 'wt-version-history-panel',

  data() {
    return {
      // storyId -> true when the user expanded the (2-line-clamped) prompt.
      expandedPrompts: {},
      // storyId -> true when the collapsed prompt is actually overflowing, so
      // the "Show more" toggle is worth showing. Populated by $_measurePrompts.
      clampedPrompts: {},
    };
  },

  computed: {
    ...mapGetters('storyVersions', [
      'panelOpen',
      'groupByStoryId',
      'versionLabel',
      'isLockedByOther',
    ]),

    openStoryId() {
      return this.$store.state.story ? this.$store.state.story.id : null;
    },

    group() {
      return this.openStoryId ? this.groupByStoryId(this.openStoryId) : null;
    },

    sortedVersions() {
      if (!this.group) {
        return [];
      }
      // Newest (highest versionNumber) first; original (v1) last.
      return this.group.versions.slice().reverse();
    },

    newestVersion() {
      if (!this.group || !this.group.versions.length) {
        return null;
      }
      return this.group.versions.reduce((max, v) =>
        (v.versionNumber || 1) > (max.versionNumber || 1) ? v : max
      );
    },

    openIsNewest() {
      return (
        this.newestVersion && this.newestVersion.storyId === this.openStoryId
      );
    },

    isViewingOlder() {
      return !!this.newestVersion && !this.openIsNewest;
    },

    newerCount() {
      if (!this.isViewingOlder) {
        return 0;
      }
      const openVersion = this.group.versions.find(
        (v) => v.storyId === this.openStoryId
      );
      const openNum = openVersion ? openVersion.versionNumber || 1 : 1;
      return this.group.versions.filter((v) => (v.versionNumber || 1) > openNum)
        .length;
    },

    newerBannerText() {
      const openVersion = this.group.versions.find(
        (v) => v.storyId === this.openStoryId
      );
      return app.culture.storyVersions.newerAvailableText
        .replace('{version}', this.versionLabel(openVersion))
        .replace('{count}', this.newerCount);
    },
  },

  mounted() {
    this.$_measurePrompts();
  },

  updated() {
    // The list re-renders on the currentTime tick (time-ago) and on group
    // refetches; re-measure so the "Show more" toggle tracks the real overflow
    // state. $_measurePrompts only writes when a value actually changes, so
    // this converges and never loops.
    this.$_measurePrompts();
  },

  methods: {
    isPromptExpanded(row) {
      return !!this.expandedPrompts[row.storyId];
    },

    isPromptClamped(row) {
      return !!this.clampedPrompts[row.storyId];
    },

    togglePrompt(row) {
      this.$set(
        this.expandedPrompts,
        row.storyId,
        !this.expandedPrompts[row.storyId]
      );
    },

    $_measurePrompts() {
      this.$nextTick(() => {
        const refs = this.$refs.promptEl;
        if (!refs) {
          return;
        }
        const els = Array.isArray(refs) ? refs : [refs];
        els.forEach((el) => {
          if (!el || !el.dataset) {
            return;
          }
          const id = el.dataset.storyId;
          // Overflow is only measurable while collapsed (an expanded row is
          // un-clamped → scrollHeight === clientHeight). Keep the cached value
          // for expanded rows so the "Show less" toggle stays visible.
          if (this.expandedPrompts[id]) {
            return;
          }
          const clamped = el.scrollHeight - el.clientHeight > 1;
          if (!!this.clampedPrompts[id] !== clamped) {
            this.$set(this.clampedPrompts, id, clamped);
          }
        });
      });
    },

    posterFor(row) {
      return row.thumbnailUrl ? app.prependHttp(row.thumbnailUrl) : null;
    },

    isLocked(row) {
      return this.isLockedByOther(row);
    },

    lockerNameFor(row) {
      return row.lockedByUserName || app.culture.storyVersions.unknownUser;
    },

    titleFor(row) {
      // TODO(versions-metadata): short version names are not in the API yet;
      // fall back to the story title.
      return `${this.versionLabel(row)} — ${row.title || 'Untitled'}`;
    },

    metaFor(row) {
      // TODO(versions-metadata): duration + aspect ratio are not in the API yet.
      const ts = app.moment(row.lastModifiedDate).valueOf();
      return app.TIME.getTimeAgo(this.$store.state.currentTime - ts);
    },

    chipFor(row) {
      const isOpen = row.storyId === this.openStoryId;
      const isNewest =
        this.newestVersion && row.storyId === this.newestVersion.storyId;
      if (isOpen && isNewest) {
        return 'current';
      }
      if (isOpen && !isNewest) {
        return 'editing';
      }
      if (isNewest && !this.openIsNewest) {
        return 'latest';
      }
      return null;
    },

    chipLabel(row) {
      return app.culture.storyVersions[this.chipFor(row)];
    },

    chipClass(row) {
      return `-${this.chipFor(row)}`;
    },

    isBranchDisabled(row) {
      // Branching the current tip is a no-op; also blocked when locked by
      // another user.
      return this.chipFor(row) === 'current' || this.isLocked(row);
    },

    close() {
      this.$store.commit('storyVersions/setPanelOpen', false);
    },

    loadVersion(row) {
      if (row.storyId === this.openStoryId || this.isLocked(row)) {
        return;
      }
      Analytics.trigger('analytics:versions:openVersion', {
        context: 'editor',
      });
      app.storyController.switchToVersion(row.storyId);
    },

    branch(row) {
      if (this.isBranchDisabled(row)) {
        return;
      }
      const folderId = this.group.folderId;

      app.dialog
        .confirm({
          type: app.dialog.types.CONFIRM_2,
          title: app.culture.storyVersions.branchConfirm,
          buttonYesText: app.culture.storyVersions.branchConfirmYes,
          buttonNoText: app.culture.generic.CANCEL,
        })
        .then((data) => {
          if (!data.action) {
            return;
          }
          app.delayedSpinner.show();
          this.$store
            .dispatch('storyVersions/BRANCH_VERSION', {
              storyId: row.storyId,
              folderId,
            })
            .then(
              () => {
                Analytics.trigger('analytics:versions:branch', {
                  context: 'editor',
                });
                app.delayedSpinner.hide();
                // BRANCH_VERSION already force-refetches the affected groups;
                // the panel re-renders reactively from the storyVersions getter.
              },
              (err) => {
                app.delayedSpinner.hide();
                if (err && err.errorCode === 400) {
                  app.dialog.error({
                    type: app.dialog.types.ERROR_1,
                    title: app.culture.storyVersions.branchRootError,
                  });
                }
              }
            );
        });
    },
  },
};
</script>
